import { Suspense, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { blogPosts, blogCategories } from '../../mocks/blogPosts';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seo';

export default function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  useSEO({
    title: 'Warrington Fintech Banking AI Blog | PURECODE Ltd Expert Insights',
    description: 'Expert insights on fintech innovation, banking technology, AI solutions, blockchain, digital payments, and financial services transformation. Stay updated with the latest trends in financial technology from PURECODE Ltd Warrington.',
    keywords: 'fintech blog, banking technology, AI banking, blockchain finance, digital payments, financial services, Warrington fintech, banking innovation, RegTech, InsurTech',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema('PURECODE Blog - Fintech & Banking Insights', 'Expert insights on fintech innovation, banking technology, and AI solutions'),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Blog', url: '/blog' }
        ])
      ]
    }
  });

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const featuredPost = blogPosts[0];

  return (
    <Suspense fallback={<div className="min-h-screen bg-white"></div>}>
      <div className="min-h-screen bg-white">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-32 pb-16 px-6 bg-gradient-to-br from-slate-50 to-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-5xl font-bold text-gray-900 mb-4">
                Fintech & Banking Insights
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Expert perspectives on AI, blockchain, digital banking, and the future of financial services
              </p>
            </div>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-12">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search articles, topics, or tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-6 py-4 pr-12 rounded-full border-2 border-gray-200 focus:border-teal-500 focus:outline-none text-base"
                />
                <i className="ri-search-line absolute right-6 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-16">
              {blogCategories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    selectedCategory === category
                      ? 'bg-teal-500 text-white shadow-md'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Featured Post */}
            {selectedCategory === 'All' && !searchQuery && (
              <Link to={`/blog/${featuredPost.slug}`} className="block mb-20 group">
                <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
                  <div className="grid md:grid-cols-2 gap-0">
                    <div className="relative h-80 md:h-auto overflow-hidden">
                      <img
                        src={featuredPost.image}
                        alt={featuredPost.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-6 left-6">
                        <span className="px-4 py-2 bg-teal-500 text-white text-sm font-semibold rounded-full">
                          Featured
                        </span>
                      </div>
                    </div>
                    <div className="p-10 flex flex-col justify-center">
                      <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                        <span className="px-3 py-1 bg-teal-50 text-teal-600 rounded-full font-medium">
                          {featuredPost.category}
                        </span>
                        <span>{featuredPost.date}</span>
                        <span>•</span>
                        <span>{featuredPost.readTime}</span>
                      </div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-4 group-hover:text-teal-600 transition-colors">
                        {featuredPost.title}
                      </h2>
                      <p className="text-gray-600 mb-6 leading-relaxed">
                        {featuredPost.excerpt}
                      </p>
                      <div className="flex items-center gap-3">
                        <span className="text-teal-600 font-semibold group-hover:gap-4 transition-all">
                          Read Article
                        </span>
                        <i className="ri-arrow-right-line text-teal-600 text-xl group-hover:translate-x-2 transition-transform"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            )}
          </div>
        </section>

        {/* Blog Grid */}
        <section className="py-16 px-6">
          <div className="max-w-7xl mx-auto">
            {filteredPosts.length === 0 ? (
              <div className="text-center py-20">
                <i className="ri-file-search-line text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">No articles found</h3>
                <p className="text-gray-600">Try adjusting your search or filter criteria</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.map(post => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
                  >
                    <div className="relative h-56 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-3 text-sm text-gray-500">
                        <span className="px-3 py-1 bg-teal-50 text-teal-600 rounded-full font-medium text-xs">
                          {post.category}
                        </span>
                        <span>{post.date}</span>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-3 leading-relaxed">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">{post.readTime}</span>
                        <span className="text-teal-600 font-semibold text-sm group-hover:gap-2 transition-all flex items-center gap-1">
                          Read More
                          <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="py-20 px-6 bg-gradient-to-br from-teal-500 to-teal-600">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-4">
              Stay Updated with Fintech Insights
            </h2>
            <p className="text-xl text-teal-50 mb-8">
              Get the latest articles on banking technology, AI, and financial innovation delivered to your inbox
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-6 py-4 rounded-full text-base focus:outline-none focus:ring-2 focus:ring-white"
                required
              />
              <button
                type="submit"
                className="px-8 py-4 bg-gray-900 text-white rounded-full font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                Subscribe Now
              </button>
            </form>
          </div>
        </section>

        <Footer />
      </div>
    </Suspense>
  );
}
